import {
  Component, Prop, Vue, Watch,
} from 'vue-property-decorator';
import { Field, Input } from '@hb/coffee';
import { checkVirtualNumber } from '@/shared/utils/validator';
import { Validator } from '@hb/finance-ui';
import { trimValue } from '@/shared/utils/trim';
import UserPersonalInfoApi from '@/api/userPersonalInfo';
import { UserVo } from '@/vo/user';
import { phoneNoMaskFormat } from '@/shared/utils/dataMaskFormat';
import { ContactsGroupContext, SupplementInfoField } from '@/components/supplement-info-fields/types';
import { emojFilter, formatPhone344, isEmptyFieldValue } from '@/components/supplement-info-fields/utils';

export interface CheckPhoneServiceResult {
  /** 手机号与注册手机号-相同 */
  sameWithUserMobile: boolean;
  /** 手机号与银行卡预留手机号-相同 */
  sameWithBankMobile: boolean;
  /** 手机号是否无效 */
  isInvalidMobile: boolean;
}

@Component({ name: 'ContactPhone' })
export default class ContactPhone extends Vue implements SupplementInfoField {
  @Prop()
  private value!: string;

  @Prop(Array) readonly contextOtherContacts!: ContactsGroupContext;

  @Prop(Boolean) readonly showMask!: boolean | undefined;

  @Prop(String) readonly commodityId!: string;

  @Prop({ type: Boolean, default: false }) readonly validateMask!: boolean;

  private contactPhone = '';

  private errorMsg = '';

  public async onValidator(): Promise<boolean> {
    const val = this.showMask ? this.value : this.contactPhone;

    /** 脱敏 && 未强制脱敏检验时，不校验 */
    if (this.showMask && !this.validateMask) return true;

    if (isEmptyFieldValue(val) || !Validator.isPhoneNo(val)) {
      this.errorMsg = '请填写正确的手机号';
      return false;
    }

    if (!checkVirtualNumber(val)) {
      this.errorMsg = '该号段不支持，请输入正确的号码';
      return false;
    }

    if (!this.checkGroupPhoneRepeate(val)) {
      this.errorMsg = '联系人手机号不能够重复';
      return false;
    }

    /** 服务端校验输入手机号的正确性：与注册手机号一致 && 与银行卡预留手机号一致 */
    const {
      sameWithUserMobile,
      sameWithBankMobile,
      isInvalidMobile,
    } = await this.getServiceCheckPhoneResult(val);

    /** 手机号不可检验无效 */
    if (isInvalidMobile) {
      this.errorMsg = '请修改为有效联系电话';
      return false;
    }

    /** 联系人手机号不能与注册用户手机号一致 */
    if (sameWithUserMobile) {
      this.errorMsg = '请勿输入本人手机号';
      return false;
    }

    /** 联系人手机号不能与用户银行卡预留手机号一致 */
    if (sameWithBankMobile) {
      this.errorMsg = '请勿填写本人银行卡预留手机号';
      return false;
    }

    return true;
  }

  private async getServiceCheckPhoneResult(phone: string): Promise<CheckPhoneServiceResult> {
    const trimPhoneNum = trimValue(phone);
    const { checkPhoneNumRes, isSamePhoneNumRes, isUnavailableRes } = await UserPersonalInfoApi
      .checkUserContact({ contactPhone: trimPhoneNum, commodityId: this.commodityId }) || {};

    return {
      sameWithUserMobile: checkPhoneNumRes === true,
      sameWithBankMobile: isSamePhoneNumRes === true,
      isInvalidMobile: isUnavailableRes === true,
    };
  }

  /** 若存在多联系人时，相互之间手机号不能重复 */
  private checkGroupPhoneRepeate(val: string): boolean {
    const phone = trimValue(val);

    if (
      Array.isArray(this.contextOtherContacts)
      && this.contextOtherContacts.some(
        (item: UserVo.ContactInfo) => trimValue(item.contactPhone) === phone,
      )) {
      return false;
    }

    this.errorMsg = '';
    return true;
  }

  private created() {
    if (this.value) {
      this.contactPhone = this.value;
    }
  }

  @Watch('value')
  private onPropsValueChange(val: string) {
    this.contactPhone = val;
  }

  private onChange(val: string) {
    if (this.showMask) {
      /**
       * 脱敏状态下的处理：
       * 1. 真实value无变化，拦截无效的change事件
       * 2. 真实value发生变化，先清空value
       */
      if (val !== phoneNoMaskFormat(this.value)) {
        /** 脱敏状态，首次输入更新时，清空value */
        this.$emit('change', '');
      }

      return;
    }

    this.errorMsg = '';
    this.$emit('change', val);
  }

  private onBlur() {
    this.onValidator();
    this.$emit('blur');
  }

  private formatter(value: string) {
    if (this.showMask) {
      return phoneNoMaskFormat(value);
    }

    return formatPhone344(emojFilter(value));
  }

  private render() {
    return (
      <Field
        label="手机号"
        errorMessage={this.errorMsg}
      >
        <Input
          v-model={this.contactPhone}
          type="text"
          placeholder="填写联系人的手机号"
          maxlength={13}
          formatter={this.formatter.bind(this)}
          on={{
            'update:modelValue': (val: string) => this.onChange(val),
          }}
          onBlur={this.onBlur.bind(this)}
        />
      </Field>
    );
  }
}
